<template>
  <div class="product">
    <div class="container">
      <div class="product__wrapper">
        <el-breadcrumb separator=">">
          <el-breadcrumb-item :to="translations.i18nRoute({ name: 'home' })">{{ t('home.title') }}</el-breadcrumb-item>
          <el-breadcrumb-item :to="translations.i18nRoute({ name: 'store', query: { categoriesSlugs: product?.category.slug }})">{{ product?.category.name }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ product?.name }}</el-breadcrumb-item>
        </el-breadcrumb>
        <div class="product__main" v-if="product">
          <div class="product__left">
            <h1 class="product__name">{{ product.name }}</h1>
            <div class="product__main-inner">
              <div class="product__price">
                <img src="@icons/currency.svg" alt="currency">
                {{ product.price }}
              </div>
              <div class="card__rating">
                <svg v-for="idx in 5" width="17" height="25" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 17L5.5 9H1L5.5 1H11.5L8.5 6H12L3 17Z" :fill="rating >= idx ? 'white' : ''" stroke="white"/>
                </svg>
              </div>
            </div>
            <div class="product__main-inner">
              <div class="product__buttons">
                <ui-button
                    class="product__button"
                    @click="overwriteCart"
                >
                  {{ isProductInCart ? t('buttons.removeFromCart') : t('buttons.addToCart') }}
                </ui-button>
                <ui-button
                    class="product__button"
                    @click="overwriteWishlist"
                >
                  {{ isProductInWishlist ? t('buttons.removeFromFavs') : t('buttons.addToFavs') }}
                </ui-button>
              </div>
              <div class="product__tech">HTML | CSS | JS</div>
            </div>
            <div class="product__description">
              <h6>{{ t('product.description') }}</h6>
              <p>{{ product.description }}</p>
            </div>
          </div>
          <img class="product__image" :src="product.images.edges[0]?.node.image" :alt="product.name">
        </div>
        <div class="product__like">
          <div class="product__like-top">
            <h6>{{ t('product.like') }}</h6>
            <div>
              <svg ref="prevButton" width="55" height="55" viewBox="0 0 55 55" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <circle cx="27.5" cy="27.5" r="27" stroke="#FF0000"/>
                <path d="M44.8457 29.4229C45.6741 29.4229 46.3457 28.7513 46.3457 27.9229C46.3457 27.0944 45.6741 26.4229 44.8457 26.4229L44.8457 29.4229ZM9.93889 26.8622C9.3531 27.448 9.3531 28.3977 9.93889 28.9835L19.4848 38.5295C20.0706 39.1152 21.0204 39.1152 21.6062 38.5295C22.1919 37.9437 22.1919 36.9939 21.6062 36.4081L13.1209 27.9229L21.6062 19.4376C22.1919 18.8518 22.1919 17.902 21.6062 17.3163C21.0204 16.7305 20.0706 16.7305 19.4848 17.3163L9.93889 26.8622ZM44.8457 27.9229L44.8457 26.4229L10.9995 26.4229L10.9995 27.9229L10.9995 29.4229L44.8457 29.4229L44.8457 27.9229Z"
                      fill="#F3F3F3"/>
              </svg>
              <svg ref="nextButton" width="55" height="55" viewBox="0 0 55 55" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <circle cx="27.5" cy="27.5" r="27" stroke="#FF0000"/>
                <path d="M44.8457 29.4229C45.6741 29.4229 46.3457 28.7513 46.3457 27.9229C46.3457 27.0944 45.6741 26.4229 44.8457 26.4229L44.8457 29.4229ZM9.93889 26.8622C9.3531 27.448 9.3531 28.3977 9.93889 28.9835L19.4848 38.5295C20.0706 39.1152 21.0204 39.1152 21.6062 38.5295C22.1919 37.9437 22.1919 36.9939 21.6062 36.4081L13.1209 27.9229L21.6062 19.4376C22.1919 18.8518 22.1919 17.902 21.6062 17.3163C21.0204 16.7305 20.0706 16.7305 19.4848 17.3163L9.93889 26.8622ZM44.8457 27.9229L44.8457 26.4229L10.9995 26.4229L10.9995 27.9229L10.9995 29.4229L44.8457 29.4229L44.8457 27.9229Z"
                      fill="#F3F3F3"/>
              </svg>
            </div>
          </div>
          <swiper
              class="swiper"
              :modules="[Navigation]"
              :navigation="{ prevEl: prevButton, nextEl: nextButton }"
              :speed="500"
              :spaceBetween="40"
              :breakpoints="{
                200: {
                  slidesPerView: 1
                },
                650: {
                  slidesPerView: 2
                },
                1000: {
                  slidesPerView: 3
                },
                1300: {
                  slidesPerView: 4
                }
              }"
          >
            <swiper-slide
                class="swiper__slide"
                v-for="product in products"
                :key="product.node.uuid"
            >
              <product-card
                  :product="product.node"
              />
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import translations from "@/core/helpers/translations.js";
import {useI18n} from "vue-i18n";
import {computed, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Products} from "@/core/api/products.js";
import UiButton from "@/components/ui/ui-button.vue";
import {Navigation} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import ProductCard from "@/components/cards/product-card.vue";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useCartStore} from "@/stores/cart.js";
import {useAuthStore} from "@/stores/auth.js";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";
import {Wishlist} from "@/core/api/wishlist.js";

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

const productSlug = computed(() => {
  return route.params.productSlug
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === product?.value?.uuid)
})
const isProductInWishlist = computed(() => {
  const el = wishlistStore.wishlist.products?.edges.find(
      (el) => el?.node?.uuid === product.value.uuid
  );

  return !!el;
});

const isLoading = ref(false)
const product = ref(null)
const products = ref([])
const prevButton = ref(null)
const nextButton = ref(null)

const rating = computed(() => {
  return product.value.feedbacks.edges.length ? product.value.feedbacks.edges[0].node.rating : 3;
})

const getProductBySlug = async () => {
  isLoading.value = true

  const res = await Products.getProductBySlug(productSlug.value)
  product.value = res.data.products.edges[0].node

  isLoading.value = false
}

const getProductsByCategory = async () => {
  const res = await Products.getProducts(
      `categoriesSlugs: "${product?.value?.category?.slug}"` +
      `, first: 12` +
      `, orderBy: "random"`
  )
  products.value = res.data.products.edges
}

const overwriteCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      await removeProductFormCart()
    } else {
      await addProductToCart()
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('awe-user-locale') || 'en-gb'
      }
    })
  }
}

const addProductToCart = async () => {
  if (user.value) {
    const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);

    if (res.errors) {
      ElNotification({
        title: t('popup.error'),
        message: res.errors[0].message,
        type: 'error'
      })
    } else {
      cartStore.setCurrentOrders(res.data.addOrderProduct?.order)

      ElNotification({
        message: t('popup.addToCart', { product: product.value.name }),
        type: 'success'
      })
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('awe-user-locale') || 'en-gb'
      }
    })
  }
}

const removeProductFormCart = async () => {
  const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);

  if (!res.errors) {
    cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
  }
}

const overwriteWishlist = async () => {
  if (user.value) {
    if (isProductInWishlist.value) {
      const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, product.value.uuid)

      if (!res.errors) {
        wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
      }
    } else {
      const res = await Wishlist.overwriteWishlist('addWishlistProduct', accessToken.value, wishlistUuid.value, product.value.uuid)

      if (res.data.addWishlistProduct) {
        wishlistStore.wishlist = res.data.addWishlistProduct.wishlist

        ElNotification({
          message: t('popup.addToWishlist', { product: product.value.name }),
          type: "success"
        });
      } else {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      }
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('awe-user-locale') || 'en-gb'
      }
    })
  }
}

watch(
    () => productSlug.value,
    async () => {
      await getProductBySlug()
      await getProductsByCategory()
    },
    { deep: true }
)

onMounted(async () => {
  await getProductBySlug()
  await getProductsByCategory()
})
</script>

<style lang="scss" scoped>
.product {
  padding-block: 100px;

  &__main {
    margin-top: 30px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 75px;

    &-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__name {
    color: #F3F3F3;
    font-size: 64px;
    font-weight: 700;
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 15px;

    color: #F3F3F3;
    font-size: 20px;

    & img {
      width: 32px;
    }
  }

  &__rating {
    display: flex;
    align-items: center;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__button {
    flex-shrink: 0;
    padding-inline: 15px;
    width: fit-content;
  }

  &__tech {
    color: #F3F3F3;
    font-size: 20px;
  }

  &__description {
    margin-top: 50px;
    display: flex;
    flex-direction: column;
    gap: 15px;

    & h6 {
      color: #F3F3F3;
      font-size: 20px;
      font-weight: 700;
      text-transform: uppercase;
    }

    & p {
      color: #F3F3F3;
      font-size: 20px;
    }
  }

  &__image {
    width: 550px;
    border-radius: 10px;
  }

  &__like {
    margin-top: 75px;

    &-top {
      margin-bottom: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      & h6 {
        color: #F3F3F3;
        font-size: 64px;
        font-weight: 700;
      }

      & div {
        display: flex;
        align-items: center;
        gap: 15px;

        & svg {
          cursor: pointer;
          transition: 0.2s;

          &:last-child {
            transform: rotate(180deg);
          }

          @include hover {
            color: #ff0000;
          }
        }
      }
    }
  }
}

.swiper {
  &__slide {
    display: grid;
    place-items: center;
  }
}

:deep(.swiper-button-disabled) {
  opacity: 0.5;
  cursor: not-allowed !important;
  
  @include hover {
    color: #000 !important;
  }
}

@media (max-width: 1100px) {
  .product {
    &__main {
      flex-direction: column-reverse;
      align-items: center;
    }

    &__like {
      &-top {
        & h6 {
          font-size: 36px;
        }
      }
    }
  }
}

@media (max-width: 600px) {
  .product {
    &__image {
      width: 100%;
    }

    &__name {
      font-size: 36px;
    }

    &__description {
      & h6 {
        font-size: 16px;
      }

      & p {
        font-size: 16px;
      }
    }

    &__main {
      &-inner {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
      }
    }

    &__like {
      &-top {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }
    }
  }
}
</style>